import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  Button,
  IconButton,
  Stack,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useOkta } from '../../hooks/oktaHook';
import VoteDetailsCard from '../voteCards/VoteDetails';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useSelector } from 'react-redux';
import { fetchSKULevelComments } from '../../pages/FeedbackCollation/servies';

function PopDrawer({ drawerOpen, toggleDrawer, rowData, rowIndex, wholeData }) {
  const { userDetails } = useOkta();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [rindex, setRIndex] = useState(0);
  const [votes, setVotes] = useState([]);
  const { userLevel } = useSelector((state) => state.feedbackCollation.stepperStatus);
  const { userId } = useSelector((state) => state.general.userInformation);

  const getSkuLevelVotingComments = useCallback(async (cycleSKUKey, userID, userLevel) => {
    setLoading(true);
    try {
      const response = await fetchSKULevelComments(cycleSKUKey, userID, userLevel);
      setVotes(response?.content);
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const wholeDataMemo = useMemo(() => wholeData, [wholeData]); //memoized whole data

  useEffect(() => {
    // calls api on drawer open to fetch votes
    if (drawerOpen && rowData && userDetails && userId && data?.CycleSKUKey) {
      getSkuLevelVotingComments(data?.CycleSKUKey, userId, userLevel);
    }
  }, [drawerOpen, userId, data, rowData]);

  useEffect(() => {
    setData(rowData);
    setRIndex(rowIndex);
  }, [rowData, rowIndex]);

  const handlePrev = () => {
    if (rindex > 0) {
      const newIndex = rindex - 1;
      setRIndex(newIndex);
      setData(wholeDataMemo[newIndex]);
    }
  };
  const handleNext = () => {
    if (rindex < wholeDataMemo.length - 1) {
      const newIndex = rindex + 1;
      setRIndex(newIndex);
      setData(wholeDataMemo[newIndex]);
    }
  };

  const handleClose = (val) => {
    toggleDrawer(val);
  };

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={(val) => handleClose(false)}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 500 },
          marginTop: '50px',
          height: 'calc(100% - 50px)',
          display: 'flex',
          flexDirection: 'column'
        }
      }}>
      {/* tpo sku name */}
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <Typography variant="h6" fontWeight={700} style={{ color: '#3A3A3A' }}>
          {data?.['sku name']}
        </Typography>
        <IconButton onClick={(val) => handleClose(false)}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* tpo sku id */}
      <Box p={2}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            padding: '8px 16px',
            gap: '8px',
            width: '100%',
            background: '#CCEAF6',
            borderRadius: '4px'
          }}>
          <span
            style={{
              fontSize: '12px',
              fontWeight: 700,
              lineHeight: '15.96px',
              textAlign: 'left'
            }}>
            UPC: {data?.['upc/gtin']}
          </span>
        </div>
      </Box>

      {/* tpo sku comments*/}
      <Box
        p={2}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          minHeight: '30vh', // 30% of viewport height
          maxHeight: '80vh', // Maximum 50% of viewport height
          height: '60vh',
          // Scrollbar styling
          '&::-webkit-scrollbar': {
            width: '6px' // Thin scrollbar
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent' // Transparent track
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.2)', // Minimal, semi-transparent thumb
            borderRadius: '3px'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(0,0,0,0.3)' // Slightly darker on hover
          }
        }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center', // Center horizontally
              alignItems: 'center', // Center vertically
              height: '60vh' // Take full viewport height
            }}>
            <CircularProgress />
            {/* <ListSkeleton listsToRender={2} /> */}
          </Box>
        ) : (
          <Stack spacing={2}>
            {votes.length == 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center', // Center horizontally
                  alignItems: 'center', // Center vertically
                  height: '60vh' // Take full viewport height
                }}>
                <Typography variant="body1" color="textSecondary">
                  There are no comments from the users for this SKU
                </Typography>
              </Box>
            ) : (
              votes.map((item, index) => {
                return (
                  <VoteDetailsCard
                    key={`${index + 1}`}
                    voter={item?.userName}
                    recommendation={item?.recommendations}
                    voteType={item?.agreeOrDisagree ? 'Agree' : 'Disagree'}
                    voteDate={item?.createdOn}
                    comment={item?.userComment}
                    functionName={item?.functionalName}
                    voterAvatarIcon={item?.voterAvatarIcon}
                  />
                );
              })
            )}
          </Stack>
        )}
      </Box>

      <Box
        p={2}
        sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'white',
          borderTop: '1px solid #ddd',
          zIndex: 10 // Ensure it stays on top of scrollable content
        }}
        display="flex"
        justifyContent="flex-end"
        alignItems="center">
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          disabled={rindex === 0}
          variant="outlined"
          onClick={handlePrev}
          sx={{ borderRadius: 20, marginRight: '8px' }}>
          Prior SKU
        </Button>
        <Button
          endIcon={<ArrowForwardOutlinedIcon />}
          disabled={rindex === wholeDataMemo.length - 1}
          variant="outlined"
          onClick={handleNext}
          sx={{ borderRadius: 20 }}>
          Next SKU
        </Button>
      </Box>
    </Drawer>
  );
}

export default PopDrawer;
